/**
 * Joy Loyalty — Points Calculator Auto-Injector
 *
 * Tự động chèn block "Earn X <currency> after purchase" sau <product-payment-container>
 * trên mọi product template. Dùng joyInstance.earnPrograms() + joyInstance.customer()
 * để tính đúng điểm theo VIP tier hiện tại của customer.
 *
 * Cách dùng: load file này trên tất cả product page (theme.liquid hoặc Custom Liquid block).
 */

(function () {
  'use strict';

  // ===== CONFIG =====
  var CONFIG = {
    fallbackIcon: 'https://cdn.shopify.com/s/files/1/0370/9537/4907/files/joy_Giraffe_Prime.svg?v=1778496399',
    textColor: 'rgb(48, 48, 48)',
    fontSize: '14px',
    alignment: 'left',              // left | center | right
    insertPosition: 'afterend',     // afterend = dưới ATC; beforebegin = trên
    hideIfZero: true,               // ẩn block nếu điểm = 0 (vd guest bị skip earn)
    joyWaitTimeoutMs: 8000
  };
  // ==================

  var INJECTED_CLASS = 'joy-points-calculator--injected';
  var joyCache = { earnPrograms: null, customer: null, loaded: false, loading: null };

  // Point terminology luôn lấy từ AVADA_JOY.settings (merchant cấu hình trong app),
  // không hard code tên currency.
  function getPointTerminology() {
    var s = (window.AVADA_JOY && window.AVADA_JOY.settings) || {};
    return {
      singular: s.pointSingular || 'point',
      plural: s.pointPlural || 'points'
    };
  }

  function formatPointText(points) {
    var t = getPointTerminology();
    return Number(points) === 1 ? t.singular : t.plural;
  }

  function waitForJoy() {
    return new Promise(function (resolve, reject) {
      if (window.joyInstance && typeof window.joyInstance.earnPrograms === 'function') return resolve(window.joyInstance);
      var start = Date.now();
      var iv = setInterval(function () {
        if (window.joyInstance && typeof window.joyInstance.earnPrograms === 'function') {
          clearInterval(iv);
          resolve(window.joyInstance);
        } else if (Date.now() - start > CONFIG.joyWaitTimeoutMs) {
          clearInterval(iv);
          reject(new Error('joyInstance not available'));
        }
      }, 150);
    });
  }

  function loadJoyData() {
    if (joyCache.loaded) return Promise.resolve(joyCache);
    if (joyCache.loading) return joyCache.loading;

    joyCache.loading = waitForJoy()
      .then(function (joy) {
        return Promise.all([
          Promise.resolve().then(function () { return joy.earnPrograms(); }).catch(function () { return []; }),
          Promise.resolve().then(function () { return joy.customer(); }).catch(function () { return null; })
        ]);
      })
      .then(function (results) {
        joyCache.earnPrograms = Array.isArray(results[0]) ? results[0] : [];
        joyCache.customer = results[1] || null;
        joyCache.loaded = true;
        return joyCache;
      })
      .catch(function () {
        joyCache.earnPrograms = [];
        joyCache.customer = null;
        joyCache.loaded = true;
        return joyCache;
      });

    return joyCache.loading;
  }

  function pickPlaceOrderProgram(programs, customer) {
    var isGuest = !customer || !customer.id;
    var hasTier = !!(customer && customer.tierId);

    var candidates = (programs || []).filter(function (p) {
      return p && p.event === 'place_order' && p.status === true && p.isDraft === false;
    });

    candidates.sort(function (a, b) {
      var pa = typeof a.priorityPlaceOrder === 'number' ? a.priorityPlaceOrder : 999;
      var pb = typeof b.priorityPlaceOrder === 'number' ? b.priorityPlaceOrder : 999;
      return pa - pb;
    });

    for (var i = 0; i < candidates.length; i++) {
      var p = candidates[i];
      if (p.appliedPlaceOrderTo === 'vip-tier' && !hasTier) continue;
      if (isGuest && p.skipEarnPointGuest) continue;
      return p;
    }
    return null;
  }

  function resolveRate(program, customer) {
    var earnPoint = Number(program.earnPoint) || 0;
    var rateMoney = Number(program.rateMoney) || 1;

    if (program.isAppliedVipTier && customer && customer.tierId &&
      program.earnPointsTiers && program.earnPointsTiers[customer.tierId]) {
      var tier = program.earnPointsTiers[customer.tierId];
      if (typeof tier.earnPoint === 'number') earnPoint = tier.earnPoint;
      if (typeof tier.rateMoney === 'number' && tier.rateMoney > 0) rateMoney = tier.rateMoney;
    }
    return { earnPoint: earnPoint, rateMoney: rateMoney || 1, roundingMethod: program.roundingMethod || 'roundingNearest' };
  }

  function roundPoints(value, method) {
    switch (method) {
      case 'roundingDown': return Math.floor(value);
      case 'roundingUp': return Math.ceil(value);
      case 'roundingNearest':
      default: return Math.round(value);
    }
  }

  function calculatePoints(priceCents, rate) {
    if (!rate || !priceCents) return 0;
    var dollars = priceCents / 100;
    var raw = (dollars / rate.rateMoney) * rate.earnPoint;
    if (!isFinite(raw) || raw <= 0) return 0;
    return roundPoints(raw, rate.roundingMethod);
  }

  function getSelectedVariantId() {
    var url = new URL(window.location.href);
    if (url.searchParams.get('variant')) return url.searchParams.get('variant');
    var input = document.querySelector('input[name="id"]:checked, select[name="id"], input[name="id"]');
    return input ? input.value : null;
  }

  function getProductPriceCents() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      var variants = window.ShopifyAnalytics.meta.product.variants || [];
      var selectedId = getSelectedVariantId();
      var match = variants.find(function (v) { return String(v.id) === String(selectedId); }) || variants[0];
      if (match && typeof match.price === 'number') return match.price;
    }
    var meta = document.querySelector('meta[property="product:price:amount"]');
    if (meta) return Math.round(parseFloat(meta.getAttribute('content')) * 100);

    var priceEl = document.querySelector('[data-product-price], .price-item--sale, .price-item--regular, .product__price');
    if (priceEl) {
      var n = priceEl.textContent.replace(/[^\d.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
      var f = parseFloat(n);
      if (!isNaN(f)) return Math.round(f * 100);
    }
    return 0;
  }

  function pickIcon(program) {
    return CONFIG.fallbackIcon;
  }

  function buildBlock(points, iconUrl) {
    var wrapper = document.createElement('div');
    wrapper.className = 'shopify-block shopify-app-block ' + INJECTED_CLASS;
    wrapper.innerHTML =
      '<div class="joy-points-calculator__block" data-alignment="' + CONFIG.alignment + '"' +
      ' style="display:flex;justify-content:' + CONFIG.alignment + ';margin-top:12px;">' +
      '<div class="Avada-PointsCalculator__Container"' +
      ' style="color:' + CONFIG.textColor + ';font-size:' + CONFIG.fontSize + ';font-family:inherit;display:flex;align-items:center;gap:8px;">' +
      '<img class="joy-points-calculator__icon" src="' + iconUrl + '" alt="points icon" style="width:36px;height:36px;flex-shrink:0;">' +
      '<p style="color:' + CONFIG.textColor + ';font-size:' + CONFIG.fontSize + ';font-family:inherit;margin:0;">' +
      '<span class="joy-points-calculator__text">Earn ' + points + ' ' + formatPointText(points) + ' after purchase.</span>' +
      '</p>' +
      '</div>' +
      '</div>';
    return wrapper;
  }

  function updateBlock(block, points, iconUrl) {
    var span = block.querySelector('.joy-points-calculator__text');
    if (span) span.textContent = 'Earn ' + points + ' ' + formatPointText(points) + ' after purchase.';
    var img = block.querySelector('.joy-points-calculator__icon');
    if (img && img.getAttribute('src') !== iconUrl) img.setAttribute('src', iconUrl);
    block.style.display = (CONFIG.hideIfZero && points <= 0) ? 'none' : '';
  }

  function injectFor(container, program, customer) {
    if (!container || !container.parentElement) return;

    var rate = program ? resolveRate(program, customer) : null;
    var points = rate ? calculatePoints(getProductPriceCents(), rate) : 0;
    var iconUrl = pickIcon(program);

    var existing = container.parentElement.querySelector('.' + INJECTED_CLASS);
    if (existing) {
      updateBlock(existing, points, iconUrl);
      return;
    }
    if (CONFIG.hideIfZero && points <= 0) return;

    container.insertAdjacentElement(CONFIG.insertPosition, buildBlock(points, iconUrl));
  }

  function injectAll() {
    var containers = Array.prototype.slice
      .call(document.querySelectorAll('product-payment-container'))
      .filter(function (c) { return !c.closest('product-sticky-form'); });
    if (!containers.length) return;

    loadJoyData().then(function (cache) {
      var program = pickPlaceOrderProgram(cache.earnPrograms, cache.customer);
      containers.forEach(function (c) { injectFor(c, program, cache.customer); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectAll);
  } else {
    injectAll();
  }

  document.addEventListener('variant:change', injectAll);
  document.addEventListener('variantChange', injectAll);
  document.addEventListener('change', function (e) {
    if (e.target && (e.target.name === 'id' || (e.target.closest && e.target.closest('variant-selects, variant-radios')))) {
      setTimeout(injectAll, 50);
    }
  });

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var node = mutations[i].addedNodes[j];
        if (node.nodeType === 1 &&
          (node.tagName === 'PRODUCT-PAYMENT-CONTAINER' ||
            (node.querySelector && node.querySelector('product-payment-container')))) {
          injectAll();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
